import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Box,
  IconButton,
} from "@mui/material";
import closeIcon from "../../../../../../images/ai_image/closeIcon.svg";
import { StoreContext } from "../../../../../store/StoreProvider";
import Act from "../../../../../store/actions";
import { prepareEditDialog } from "../utils/prepareEditDialog";
import { editDialogConfig } from "../constants/textConfig";
import {
  createCustomEvents,
  updateCustomEvents,
  deleteCustomEvents,
} from "../utils/crudEvents";

const textFieldStyles = {
  "& .MuiOutlinedInput-root": {
    height: "45px",
    "& fieldset": {
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#3BBFF0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#3BBFF0",
    },
  },
};

const dialogStyles = {
  "& .MuiDialog-container": {
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: "600px",
      height: "100%",
      maxHeight: "303px",
      padding: "16px",
      borderRadius: "8px",
      position: "absolute",
      top: "20%",
    },
  },
};

const dialogTitleStyles = {
  textAlign: "center",
  fontSize: "20px",
  fontWeight: "700",
  marginBottom: "24px",
  padding: "0",
};

const actionButtonStyles = (type) => ({
  width: "49%",
  height: "39px",
  padding: "8px 24px",
  background:
    // eslint-disable-next-line no-nested-ternary
    type === editDialogConfig.deleteRow.name ||
    type === editDialogConfig.deleteEvn.name
      ? "#F83B3B"
      : type === ""
      ? "transparent"
      : "linear-gradient(90deg, #38BBF8 0%, #0EA2E9 100%)",
  color: "white",
  boxShadow: "0px 0px 16px 0px rgba(59, 191, 240, 0.2)",
  "&:hover": (type === editDialogConfig.deleteRow.name ||
    type === editDialogConfig.deleteEvn.name) && {
    backgroundColor: "#F83B3B",
  },
});

const EditDialog = () => {
  const [state, dispatch] = useContext(StoreContext);
  const { sna, user } = state;
  const { editDialog } = sna;
  const { tableData } = sna.AI;
  const events = sna.filter.eventTypes.initial;
  const [input, setInput] = useState("");
  const {
    deleteRow,
    deleteEvn,
    updateMainEvn,
    updateSubEvn,
    addMainEvn,
    addSubEvn,
  } = editDialogConfig;

  const handleClose = () => {
    setInput("");
    prepareEditDialog(dispatch, "closeDialog");
  };

  const handleInputBlur = (e) => {
    setInput(e.target.value);
  };

  const handleDeleteRow = () => {
    if (editDialog.deletedTableData.length >= 0) {
      dispatch({
        type: Act.SNA_SET_AI_TABLE_DATA,
        payload: editDialog.deletedTableData,
      });
    }
  };

  const handleUpdateEvent = () => {
    if (!input) {
      alert("輸入欄不得為空");
      return;
    }

    const foundRepeatEvn = events.find((e) => e.name === input);
    if (foundRepeatEvn) {
      alert(`「${input}」 已在列表中`);
      return;
    }

    updateCustomEvents(dispatch, user, editDialog.evtTypeId, input);
  };

  const handleDeleteEvent = () => {
    const filteredEvents = events
      .filter(
        (el) =>
          el.evtTypeId === editDialog.evtTypeId ||
          el.pid === editDialog.evtTypeId
      )
      .map((el) => el.evtTypeId);
    deleteCustomEvents(dispatch, user, filteredEvents);
  };

  const handleCreateEvent = () => {
    if (!input) {
      alert("輸入欄不得為空");
      return;
    }

    const foundRepeatEvn = events.find((el) => el.name === input);

    const findInputInSubEvents = () => {
      const foundCurMainEvn = events.find(
        (el) => el.name === editDialog.curMainEvn
      );
      const filteredCurSubEvns = events.filter(
        (el) => el.pid === foundCurMainEvn.evtTypeId
      );
      const foundInputInCurSubEvns = filteredCurSubEvns.find(
        (el) => el.name === input
      );
      return foundInputInCurSubEvns;
    };

    if (foundRepeatEvn) {
      alert(`「${input}」 已在列表中`);

      const newTableData = tableData.map((el) => {
        if (el.id === editDialog.curRowId) {
          if (
            editDialog.type === addMainEvn.name &&
            foundRepeatEvn.pid === "EVTTYPE0"
          ) {
            return { ...el, mainEvn: input };
          }
          if (
            editDialog.type === addSubEvn.name &&
            foundRepeatEvn.pid !== "EVTTYPE0" &&
            findInputInSubEvents()
          ) {
            return { ...el, subEvn: input };
          }
        }
        return el;
      });

      dispatch({ type: Act.SNA_SET_AI_TABLE_DATA, payload: newTableData });

      return;
    }

    const eventNumbers = events.map((event) =>
      parseInt(event.evtTypeId.replace("EVTTYPE", ""), 10)
    );

    const filteredEventNumbers = eventNumbers.filter(
      (eventNumber) => !Number.isNaN(eventNumber)
    );

    const maxNumber = Math.max(...filteredEventNumbers);

    const newNumber = maxNumber + 1;

    const customEvents = {
      evtTypeId: `EVTTYPE${newNumber}`,
      id: `EVTTYPE${newNumber}`,
      type: "EventType",
      name: input,
      label: input,
      pid:
        editDialog.type === addSubEvn.name
          ? events.find((el) => el.name === editDialog.curMainEvn).evtTypeId
          : "EVTTYPE0",
      checked: false,
      step: editDialog.type === addSubEvn.name ? 2 : 1,
    };

    createCustomEvents(dispatch, user, customEvents);

    const newTableData = tableData.map((el) => {
      if (el.id !== editDialog.curRowId) {
        return el;
      }

      const updatedEl = { ...el };

      if (editDialog.type === addMainEvn.name) {
        updatedEl.mainEvn = input;
        updatedEl.subEvn = "";
      } else if (editDialog.type === addSubEvn.name) {
        updatedEl.subEvn = input;
      }

      return updatedEl;
    });

    dispatch({ type: Act.SNA_SET_AI_TABLE_DATA, payload: newTableData });
  };

  const handleClick = () => {
    switch (editDialog.type) {
      case deleteRow.name:
        handleDeleteRow();
        break;
      case updateMainEvn.name:
      case updateSubEvn.name:
        handleUpdateEvent();
        break;
      case deleteEvn.name:
        handleDeleteEvent();
        break;
      default:
        handleCreateEvent();
    }
    handleClose();
  };

  useEffect(() => {
    if (editDialog.type === updateMainEvn.name) {
      setInput(editDialog.curMainEvn);
    }

    if (editDialog.type === updateSubEvn.name) {
      setInput(editDialog.curSubEvn);
    }
  }, [editDialog, updateSubEvn]);

  return (
    <Dialog open={editDialog.open} onClose={handleClose} sx={dialogStyles}>
      <Box sx={{ display: "flex", justifyContent: "end", width: "100%" }}>
        <IconButton onClick={handleClose}>
          <img src={closeIcon} alt="close" />
        </IconButton>
      </Box>
      <DialogTitle sx={dialogTitleStyles}>{editDialog.title}</DialogTitle>
      <DialogContent sx={{ padding: "0 24px" }}>
        <DialogContentText sx={{ color: "black", marginBottom: "8px" }}>
          {editDialog.content}
          {editDialog.type !== deleteRow.name &&
            editDialog.type !== deleteEvn.name && (
              <span style={{ color: "red" }}>{editDialogConfig.required}</span>
            )}
        </DialogContentText>
        {editDialog.type !== deleteRow.name &&
          editDialog.type !== deleteEvn.name && (
            <TextField
              onBlur={handleInputBlur}
              sx={textFieldStyles}
              fullWidth
              placeholder={editDialogConfig.placeholder}
              defaultValue={
                // eslint-disable-next-line no-nested-ternary
                editDialog.type === updateMainEvn.name
                  ? editDialog.curMainEvn
                  : editDialog.type === updateSubEvn.name
                  ? editDialog.curSubEvn
                  : ""
              }
            />
          )}
      </DialogContent>
      <DialogActions sx={{ padding: "24px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              width: "49%",
              height: "39px",
              border: "1px solid #E7E7E7",
              padding: "8px 24px",
              color: "black",
            }}
          >
            {editDialogConfig.cancelTitle}
          </Button>
          <Button
            onClick={handleClick}
            sx={actionButtonStyles(editDialog.type)}
          >
            {
              // eslint-disable-next-line no-nested-ternary
              editDialog.type === deleteRow.name ||
              editDialog.type === deleteEvn.name
                ? editDialogConfig.deleteTitle
                : editDialog.type === ""
                ? ""
                : editDialogConfig.addNewTitle
            }
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default EditDialog;
